import { AnyComponent, Component } from 'preact'
import AlertComponent from '../ui-components/AlertComponent'
import Overview, { DataSourceOpts, Filters, StateAwareHeaderComponentProps } from '../overview'
import { getColumns, RoleList, BadgeList, RankList, CustomProfileFieldList, Group } from './Columns'
import { getFilters } from './Filters'
import {
  fetchData,
  UserData
} from './DataSource'
import HeaderActionsComponent from './HeaderActions/HeaderActionsComponent'
import { common } from '@insided/deprecated-preact-components/common'
import getBulkActions from './BulkActions'
import EmptyPage from '@insided/deprecated-preact-components/empty-page/EmptyPage'
import globals from '@insided/deprecated-utils/globals'
import { Model } from '@insided/deprecated-preact-components/table/types'
import { FetchUsersIdsProps } from '../EmailCampaigns/components/Metrics'

interface FlashMessages {
  [key: string]: string
}

export interface UserOverviewProps {
  flashMessages: FlashMessages
  customProfileFields: CustomProfileFieldList
  roles: RoleList
  badges: BadgeList
  ranks: RankList
  groups: Group[]
  type?: string
  title?: string
  source?: string
  onTitleChanged?: (newValue: string) => void
  defaultFilters?: Filters
  onDataFetched: (response: { data: UserData[], total: number }) => void
  // [Preact X] Change AnyComponent to ComponentType
  StateAwareHeaderComponent?: AnyComponent<StateAwareHeaderComponentProps<UserData>>
  // [Preact X] Change AnyComponent to ComponentType
  StateAwareDashboardComponent?: AnyComponent<StateAwareHeaderComponentProps<UserData>>
  withHeader?: boolean
  withMultiSelect?: boolean
  showFilters?: boolean
  withActionElements?: boolean
  totalUsers?: number
  onPaginationAction?: (({ page, pageSize, callback }: FetchUsersIdsProps) => void) | null
  model?: Model[] | null
}

class UserOverview extends Component<UserOverviewProps> {
  rootComponent?: Overview

  private readonly getFlashMessages = (): JSX.Element => {
    const { flashMessages } = this.props
    const alertMessages = []

    for (const type of Object.keys(flashMessages)) {
      alertMessages.push(
        <AlertComponent
          messageType={type}
          message={flashMessages[type]}
          isVisibleFor={5000}
        />
      )
    }

    return (<div>{alertMessages}</div>)
  }

  resetFilters = () => {
    this.rootComponent?.handleClearFilterCriteria()
  }

  dataSource = async (options: DataSourceOpts) => {
    const { customProfileFields, onDataFetched, totalUsers } = this.props
    
    const data = await fetchData({ ...options }, customProfileFields)

    if (options.filters?.usersIds?.length && totalUsers) {
      data.total = totalUsers
    }

    onDataFetched?.(data)

    return data
  }

  render() {
    const {
      roles,
      badges,
      ranks,
      groups,
      customProfileFields,
      type,
      title,
      source = 'User Overview',
      defaultFilters,
      StateAwareHeaderComponent,
      StateAwareDashboardComponent,
      withHeader = true,
      withMultiSelect = true,
      showFilters = true,
      withActionElements = true,
      onPaginationAction = null,
      model = null
    } = this.props

    const pagination = {
      page: 1,
      pageSize: 25,
      pageSizes: [10, 25, 50, 100]
    }
    const sort = { join_date: 'desc' }
    const emptyPageImageUrl: string = `${globals('basePath')}images/empty_result.png`

    return (
      <Overview<UserData>
        ref={node => { this.rootComponent = node }}
        type={type ?? 'UserOverview'}
        headerTitle={title ?? 'User Overview'}
        onTitleChanged={this.props.onTitleChanged}
        message={this.getFlashMessages()}
        rowId='user_id'
        model={model ?? getColumns(badges, ranks, roles, groups, customProfileFields)}
        withActionElements={withActionElements}
        withColumns
        withSearch
        withHeader={withHeader}
        showFilters={showFilters}
        searchPlaceholder='Search for username or email'
        dataSource={this.dataSource}
        withPagination={pagination}
        withSorting={sort}
        withFilters={getFilters(badges, ranks, roles, groups, customProfileFields)}
        onPaginationAction={onPaginationAction}
        entity='users'
        EmptyStateComponent={(
          <div>
            <EmptyPage
              img_src={emptyPageImageUrl}
              header='No results found'
              text={
                'Sorry, that filter combination has no results.\n Please try different criteria.'
              }
            />
          </div>
        )}
        withMultiSelect={withMultiSelect}
        defaultFilteringOptions={defaultFilters && {
          pagination,
          sort,
          filters: defaultFilters
        }}
        bulkActions={getBulkActions(roles, source)}
        StateAwareHeaderComponent={StateAwareHeaderComponent ?? (common.isModuleActive('userSegments') ? props => (
          <HeaderActionsComponent
            customProfileFields={customProfileFields}
            {...props}
          />
        ) : undefined)}
        StateAwareDashboardComponent={StateAwareDashboardComponent}
      />
    )
  }
}

export default UserOverview
